import { createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from '@store/index'
import {
  ICallFlow,
  IClinicE911Address,
  IClinicInternalExtension,
  IClinicNumber,
  IClinicPhoneDevice,
  ISipCredentialsOnce,
  ITwilioPortRequest,
  RegisterE911TwilioPayload,
  E911TwilioRegistrationResult,
} from '@dataTypes/clinic.type'
import {
  ITwilioPortability,
  PortRequestChannel,
  PortRequestProvider,
  PortRequestReviewStatus,
  PortRequestSourceModule,
  PortRequestStatus,
} from '@dataTypes/twilio.types'
import { phoneManagementAPI } from '@services/api'
import {
  createPortDocument,
  createPortRequest,
  generateReadOnlyUrl,
  generateUploadUrl,
  getAllPortRequests,
  getPortRequestStatusFromTwilio,
  resubmitPortRequest,
  updatePortRequest,
  uploadFile,
} from '@services/api/clinic.api'
import { logger } from '@utils/safeLogger'
import {
  createCallFlowRequest,
  deleteCallFlowRequest,
  getCallFlows,
  updateCallFlowRequest,
} from '@services/api/callFlow.api'
import {
  releaseTwilioNumber,
  type BulkWorkingHoursItemRequest,
} from '@services/api/phoneManagement.api'
import { normalizePhoneNumber } from '@utils/numberFormatter.util'
import {
  buildSinchPortProviderPayload,
  buildTwilioPortProviderPayload,
  toPortRequestClinicSnapshot,
} from '@utils/portRequestPayload.util'
import { BLOB_FOLDERS } from '@constants/Azure'

type RejectValue = string

const getLoggedInRequester = (): {
  requested_by_user_id: string | null
  requested_by_name: string | null
} => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}') as {
      user_id?: string
      username?: string
    }
    return {
      requested_by_user_id: user.user_id?.trim() || null,
      requested_by_name: user.username?.trim() || null,
    }
  } catch {
    return { requested_by_user_id: null, requested_by_name: null }
  }
}

const fail = (message?: string): string => message || 'Request failed'

const normalizeTwilioStatusKey = (s: string): string =>
  s
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '')

function extractTwilioPortStatus(raw: unknown): string {
  if (!raw || typeof raw !== 'object') return 'Unknown'
  const o = raw as Record<string, unknown>
  const v = o.port_in_request_status ?? o.portInRequestStatus ?? o.status
  if (typeof v === 'string') return v
  return String(v ?? 'Unknown')
}

function isTwilioPortCompleted(twilioStatus: string): boolean {
  const s = twilioStatus.trim().toLowerCase()
  return s.includes('complete')
}

export const fetchPhoneManagementData = createAsyncThunk<
  {
    clinicNumbers: IClinicNumber[]
    internalExtensions: IClinicInternalExtension[]
    e911Addresses: IClinicE911Address[]
    phoneDevices: IClinicPhoneDevice[]
    portRequests: ITwilioPortRequest[]
  },
  string,
  { rejectValue: RejectValue }
>(
  'settingsCommunication/fetchPhoneManagementData',
  async (clinicId, { rejectWithValue }) => {
    try {
      const [numbersRes, extRes, e911Res, devicesRes, portRequestsRes] =
        await Promise.all([
          phoneManagementAPI.getClinicNumbers(),
          clinicId
            ? phoneManagementAPI.getInternalExtensions(clinicId)
            : Promise.resolve({
                statusCode: 200,
                message: 'ok',
                data: { internal_extensions: [] as IClinicInternalExtension[] },
              }),
          clinicId
            ? phoneManagementAPI.getClinicE911Addresses(clinicId)
            : Promise.resolve({
                statusCode: 200,
                message: 'ok',
                data: { e911_addresses: [] as IClinicE911Address[] },
              }),
          clinicId
            ? phoneManagementAPI.getPhoneDevices(clinicId)
            : Promise.resolve({
                statusCode: 200,
                message: 'ok',
                data: { phone_devices: [] as IClinicPhoneDevice[] },
              }),
          clinicId
            ? getAllPortRequests({ is_latest: true })
            : Promise.resolve({
                statusCode: 200,
                message: 'ok',
                data: { twilio_port_requests: [] as ITwilioPortRequest[] },
              }),
        ])

      if (numbersRes.statusCode >= 400) {
        return rejectWithValue(fail(numbersRes.message))
      }
      if (extRes.statusCode >= 400) {
        return rejectWithValue(fail(extRes.message))
      }
      if (e911Res.statusCode >= 400) {
        return rejectWithValue(fail(e911Res.message))
      }
      if (devicesRes.statusCode >= 400) {
        return rejectWithValue(fail(devicesRes.message))
      }
      if (portRequestsRes.statusCode >= 400) {
        return rejectWithValue(fail(portRequestsRes.message))
      }

      return {
        clinicNumbers: (numbersRes.data?.clinic_numbers || []).filter(
          (n) => n.status !== 'Released',
        ),
        internalExtensions: extRes.data?.internal_extensions || [],
        e911Addresses: e911Res.data?.e911_addresses || [],
        phoneDevices: devicesRes.data?.phone_devices || [],
        portRequests: portRequestsRes.data?.twilio_port_requests || [],
      }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch phone data',
      )
    }
  },
)

export const purchaseTwilioNumberAction = createAsyncThunk<
  IClinicNumber,
  {
    phoneNumber: string
    friendlyName: string
    sid: string
    token: string
    clinicId: string
    contactMethods?: string | null
  },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/purchaseTwilioNumber',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const normalizedPhoneNumber = normalizePhoneNumber(payload.phoneNumber)
      const purchaseRes = await phoneManagementAPI.purchaseTwilioNumberExternal(
        normalizedPhoneNumber,
        payload.friendlyName,
        payload.sid,
        payload.token,
      )
      if (
        purchaseRes.statusCode >= 400 ||
        !purchaseRes.data?.purchased_number
      ) {
        return rejectWithValue(fail(purchaseRes.message))
      }
      const purchased = purchaseRes.data.purchased_number
      const clinicNumber: IClinicNumber = {
        clinic_id: payload.clinicId,
        phone_number: normalizedPhoneNumber,
        phone_carrier: 'Twilio',
        is_verified: false,
        is_portable: false,
        number_sid: purchased.phoneNumberSid || '',
        status: 'Active',
        purchased_date: new Date().toISOString(),
        ...(payload.contactMethods
          ? { contact_methods: payload.contactMethods }
          : {}),
      }
      const createRes =
        await phoneManagementAPI.createClinicNumber(clinicNumber)
      if (createRes.statusCode >= 400 || !createRes.data?.clinic_number) {
        return rejectWithValue(fail(createRes.message))
      }
      await dispatch(fetchPhoneManagementData(payload.clinicId)).unwrap()
      return createRes.data.clinic_number
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to purchase Twilio number',
      )
    }
  },
)

export const deleteClinicNumberAction = createAsyncThunk<
  string,
  string,
  { rejectValue: RejectValue }
>(
  'settingsCommunication/deleteClinicNumber',
  async (id, { rejectWithValue }) => {
    try {
      const res = await phoneManagementAPI.deleteClinicNumber(id)
      if (res.statusCode >= 400) {
        return rejectWithValue(fail(res.message))
      }
      return id
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to delete clinic number',
      )
    }
  },
)

export const updateClinicNumberAction = createAsyncThunk<
  IClinicNumber,
  { id: string; data: Partial<IClinicNumber> },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/updateClinicNumber',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await phoneManagementAPI.updateClinicNumber(id, data)
      if (res.statusCode >= 400 || !res.data?.clinic_number) {
        return rejectWithValue(fail(res.message))
      }
      return res.data.clinic_number
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to update clinic number',
      )
    }
  },
)

export const bulkUpdateClinicNumbersWorkingHoursAction = createAsyncThunk<
  IClinicNumber[],
  BulkWorkingHoursItemRequest[],
  { rejectValue: RejectValue }
>(
  'settingsCommunication/bulkUpdateClinicNumbersWorkingHours',
  async (items, { rejectWithValue }) => {
    try {
      const res =
        await phoneManagementAPI.bulkUpdateClinicNumbersWorkingHours(items)
      if (res.statusCode >= 400 || !res.data?.clinic_numbers) {
        return rejectWithValue(fail(res.message))
      }
      return res.data.clinic_numbers
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to bulk update working hours',
      )
    }
  },
)

export const createPhoneDeviceAction = createAsyncThunk<
  {
    phone_device: IClinicPhoneDevice
    sip_credentials_once?: ISipCredentialsOnce
  },
  IClinicPhoneDevice & { tenant_name?: string },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/createPhoneDevice',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await phoneManagementAPI.createPhoneDevice(payload)
      if (res.statusCode >= 400 || !res.data?.phone_device) {
        return rejectWithValue(fail(res.message))
      }
      return {
        phone_device: res.data.phone_device,
        sip_credentials_once: res.data.sip_credentials_once,
      }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to create phone device',
      )
    }
  },
)

export const updatePhoneDeviceAction = createAsyncThunk<
  IClinicPhoneDevice,
  { id: string; data: Partial<IClinicPhoneDevice> },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/updatePhoneDevice',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await phoneManagementAPI.updatePhoneDevice(id, data)
      if (res.statusCode >= 400 || !res.data?.phone_device) {
        return rejectWithValue(fail(res.message))
      }
      return res.data.phone_device
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to update phone device',
      )
    }
  },
)

export const deletePhoneDeviceAction = createAsyncThunk<
  string,
  { id: string; clinic_id?: string },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/deletePhoneDevice',
  async ({ id, clinic_id }, { rejectWithValue }) => {
    try {
      const res = await phoneManagementAPI.deletePhoneDevice(id, clinic_id)
      if (res.statusCode >= 400) {
        return rejectWithValue(fail(res.message))
      }
      return id
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to delete phone device',
      )
    }
  },
)

export const regenerateDeviceSipCredentialsAction = createAsyncThunk<
  ISipCredentialsOnce,
  { id: string; clinic_id: string },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/regenerateDeviceSipCredentials',
  async ({ id, clinic_id }, { rejectWithValue }) => {
    try {
      const res = await phoneManagementAPI.regenerateDeviceSipCredentials(
        id,
        clinic_id,
      )
      if (res.statusCode >= 400 || !res.data?.sip_credentials_once) {
        return rejectWithValue(fail(res.message))
      }
      return res.data.sip_credentials_once
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to regenerate SIP credentials',
      )
    }
  },
)

export const ringPhoneDeviceAction = createAsyncThunk<
  { callSid: string | null },
  { id: string; clinic_id: string; from_number?: string },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/ringPhoneDevice',
  async ({ id, clinic_id, from_number }, { rejectWithValue }) => {
    try {
      const res = await phoneManagementAPI.ringPhoneDevice(
        id,
        clinic_id,
        from_number,
      )
      if (res.statusCode >= 400) {
        return rejectWithValue(fail(res.message))
      }
      return { callSid: res.data?.callSid ?? null }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to ring device',
      )
    }
  },
)

export const createDeviceE911AddressAction = createAsyncThunk<
  {
    e911_address: IClinicE911Address
    validation_status: 'pending' | 'verified' | 'failed'
    error_message?: string
  },
  {
    clinic_id: string
    clinic_phone_device_id: string
    customer_name: string
    address: string
    city: string
    state: string
    zip: string
    iso_country?: string
  },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/createDeviceE911Address',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await phoneManagementAPI.createDeviceE911Address(payload)
      if (res.statusCode >= 400 || !res.data?.e911_address) {
        return rejectWithValue(fail(res.message))
      }
      return {
        e911_address: res.data.e911_address,
        validation_status: res.data.validation_status ?? 'pending',
        error_message: res.data.error_message,
      }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to create E911 address',
      )
    }
  },
)

export const deleteInternalExtensionAction = createAsyncThunk<
  string,
  string,
  { rejectValue: RejectValue }
>(
  'settingsCommunication/deleteInternalExtension',
  async (id, { rejectWithValue }) => {
    try {
      const res = await phoneManagementAPI.deleteInternalExtension(id)
      if (res.statusCode >= 400) {
        return rejectWithValue(fail(res.message))
      }
      return id
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to delete internal extension',
      )
    }
  },
)

export const createInternalExtensionAction = createAsyncThunk<
  IClinicInternalExtension,
  IClinicInternalExtension,
  { rejectValue: RejectValue }
>(
  'settingsCommunication/createInternalExtension',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await phoneManagementAPI.createInternalExtension(payload)
      if (res.statusCode >= 400 || !res.data?.internal_extension) {
        return rejectWithValue(fail(res.message))
      }
      return res.data.internal_extension
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to create internal extension',
      )
    }
  },
)

export const updateInternalExtensionAction = createAsyncThunk<
  IClinicInternalExtension,
  { id: string; data: Partial<IClinicInternalExtension> },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/updateInternalExtension',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await phoneManagementAPI.updateInternalExtension(id, data)
      if (res.statusCode >= 400 || !res.data?.internal_extension) {
        return rejectWithValue(fail(res.message))
      }
      return res.data.internal_extension
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to update internal extension',
      )
    }
  },
)

export const deleteE911AddressAction = createAsyncThunk<
  string,
  string,
  { rejectValue: RejectValue }
>(
  'settingsCommunication/deleteE911Address',
  async (id, { rejectWithValue }) => {
    try {
      const res = await phoneManagementAPI.deleteClinicE911Address(id)
      if (res.statusCode >= 400) {
        return rejectWithValue(fail(res.message))
      }
      return id
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to delete E911 address',
      )
    }
  },
)

export const createE911AddressAction = createAsyncThunk<
  IClinicE911Address,
  IClinicE911Address,
  { rejectValue: RejectValue }
>(
  'settingsCommunication/createE911Address',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await phoneManagementAPI.createClinicE911Address(payload)
      if (res.statusCode >= 400 || !res.data?.e911_address) {
        return rejectWithValue(fail(res.message))
      }
      return res.data.e911_address
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to create E911 address',
      )
    }
  },
)

export const updateE911AddressAction = createAsyncThunk<
  IClinicE911Address,
  { id: string; data: Partial<IClinicE911Address> },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/updateE911Address',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await phoneManagementAPI.updateClinicE911Address(id, data)
      if (res.statusCode >= 400 || !res.data?.e911_address) {
        return rejectWithValue(fail(res.message))
      }
      return res.data.e911_address
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to update E911 address',
      )
    }
  },
)

export const registerE911WithTwilioAction = createAsyncThunk<
  E911TwilioRegistrationResult,
  { clinicNumberId: string; payload: RegisterE911TwilioPayload },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/registerE911WithTwilio',
  async ({ clinicNumberId, payload }, { rejectWithValue }) => {
    try {
      const res = await phoneManagementAPI.registerClinicE911AddressWithTwilio(
        clinicNumberId,
        payload,
      )
      if (res.statusCode >= 400) {
        return rejectWithValue(fail(res.message))
      }
      return res.data
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to register E911',
      )
    }
  },
)

export const checkPortabilityAction = createAsyncThunk<
  ITwilioPortability[] | undefined,
  string | string[],
  { rejectValue: RejectValue }
>(
  'settingsCommunication/checkPortability',
  async (phoneNumbers, { rejectWithValue }) => {
    try {
      const list = Array.isArray(phoneNumbers) ? phoneNumbers : [phoneNumbers]
      const res = await phoneManagementAPI.checkPortabilityExternal(list)
      if (res.statusCode >= 400) {
        return rejectWithValue(fail(res.message))
      }
      return res.data?.portability
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to check portability',
      )
    }
  },
)

export const sendPortOtpAction = createAsyncThunk<
  Record<string, unknown>,
  { phoneNumber: string; channel: 'sms' | 'call' },
  { rejectValue: RejectValue }
>('settingsCommunication/sendPortOtp', async (payload, { rejectWithValue }) => {
  try {
    const res = await phoneManagementAPI.sendPortOtpExternal(
      payload.phoneNumber,
      payload.channel,
    )
    if (res.statusCode >= 400) {
      return rejectWithValue(fail(res.message))
    }
    return res.data
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to send OTP',
    )
  }
})

export const verifyPortOtpAction = createAsyncThunk<
  { isVerified: boolean },
  { phoneNumber: string; otp: string },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/verifyPortOtp',
  async ({ phoneNumber, otp }, { rejectWithValue }) => {
    try {
      const res = await phoneManagementAPI.verifyPortOtpExternal(
        phoneNumber,
        otp,
      )
      if (res.statusCode >= 400) {
        return rejectWithValue(fail(res.message))
      }
      return { isVerified: !!res.data?.isVerified }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to verify OTP',
      )
    }
  },
)

export const uploadPortingDocumentAction = createAsyncThunk<
  string | null,
  FormData,
  { rejectValue: RejectValue }
>(
  'settingsCommunication/uploadPortingDocument',
  async (formData, { rejectWithValue }) => {
    try {
      const res =
        await phoneManagementAPI.uploadPortingDocumentExternal(formData)
      if (res.statusCode >= 400) {
        return rejectWithValue(fail(res.message))
      }
      return res.data?.document?.sid ?? null
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to upload document',
      )
    }
  },
)

export const createPortRequestAction = createAsyncThunk<
  Record<string, unknown> | undefined,
  {
    phone_number: string
    document_sids: string[]
    user_fullname: string
    user_email: string
    company_customer_name: string
    customer_type?: string
    admin_email?: string
  },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/createPortRequest',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await phoneManagementAPI.createPortRequestExternal(payload)
      if (res.statusCode >= 400) {
        return rejectWithValue(fail(res.message))
      }
      return res.data?.twilio_port_request
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to create port request',
      )
    }
  },
)

export const submitPortRequestDbAction = createAsyncThunk<
  ITwilioPortRequest,
  {
    clinicId: string
    clinicName: string
    clinicSnapshot?: ReturnType<typeof toPortRequestClinicSnapshot>
    billingStatementFile: File
    phone_numbers: string[]
    phone_carrier: string
    representative_name: string
    representative_email: string
    wireless_phone_number: string | null
    wireless_account_number: string | null
    wireless_pin_or_ssn: string | null
  },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/submitPortRequestDb',
  async (payload, { rejectWithValue }) => {
    try {
      const targetPortDate = new Date().toISOString()
      const originalName = payload.billingStatementFile.name.replace(/ /g, '_')
      const filename =
        `billing-statement-${payload.clinicName}-${Date.now()}-${originalName}`
          .toLowerCase()
          .replace(/ /g, '_')

      const uploadUrlResponse = await generateUploadUrl(
        payload.billingStatementFile.type || 'application/pdf',
        filename,
        {
          folder: BLOB_FOLDERS.PORT_DOCUMENTS,
          clinic_id: payload.clinicId,
        },
      )
      if (uploadUrlResponse.statusCode !== 200) {
        return rejectWithValue(
          fail(uploadUrlResponse.message || 'Failed to get upload URL'),
        )
      }

      await uploadFile(
        uploadUrlResponse.data.upload_url,
        payload.billingStatementFile,
      )

      const readOnlyUrlResponse = await generateReadOnlyUrl(
        uploadUrlResponse.data.blob_name || filename,
      )
      if (readOnlyUrlResponse.statusCode !== 200) {
        return rejectWithValue(
          fail(
            readOnlyUrlResponse.message || 'Failed to generate read-only URL',
          ),
        )
      }

      const documentResponse = await createPortDocument(
        payload.clinicId,
        `PENDING_ADMIN_DOC_${Date.now()}`,
        'billing_statement',
        readOnlyUrlResponse.data.read_only_url,
      )
      if (documentResponse.statusCode !== 201) {
        return rejectWithValue(
          fail(documentResponse.message || 'Failed to save document'),
        )
      }

      const twilioPortDocumentId =
        documentResponse.data.twilio_port_document?.twilio_port_document_id
      if (!twilioPortDocumentId) {
        return rejectWithValue('Missing document ID from saved record')
      }

      const clinicSnapshot =
        payload.clinicSnapshot ||
        toPortRequestClinicSnapshot({
          clinic_name: payload.clinicName,
          address: '',
          address_line_2: null,
          city: '',
          state: '',
          zip: '',
          country_code: 'US',
        })
      if (!clinicSnapshot) {
        return rejectWithValue(
          'Clinic details are required to submit a port request.',
        )
      }

      const response = await createPortRequest({
        clinic_id: payload.clinicId,
        phone_numbers: payload.phone_numbers,
        phone_carrier: payload.phone_carrier,
        twilio_port_document_id: twilioPortDocumentId,
        twilio_request_sid: null,
        representative_name: payload.representative_name,
        representative_email: payload.representative_email,
        wireless_phone_number: payload.wireless_phone_number,
        wireless_account_number: payload.wireless_account_number,
        wireless_pin_or_ssn: payload.wireless_pin_or_ssn,
        status: PortRequestStatus['In Progress'],
        twilio_status: 'Pending',
        target_port_date: targetPortDate,
        channel: PortRequestChannel.phone,
        provider: PortRequestProvider.twilio,
        source_module: PortRequestSourceModule.phone_management,
        review_status: PortRequestReviewStatus.requested,
        ...getLoggedInRequester(),
        provider_payload: buildTwilioPortProviderPayload({
          clinic: clinicSnapshot,
          phone_numbers: payload.phone_numbers,
          phone_carrier: payload.phone_carrier,
          representative_name: payload.representative_name,
          representative_email: payload.representative_email,
          wireless_phone_number: payload.wireless_phone_number,
          wireless_account_number: payload.wireless_account_number,
          wireless_pin_or_ssn: payload.wireless_pin_or_ssn,
          twilio_port_document_id: twilioPortDocumentId,
          document_sids: [],
        }),
      })

      if (response.statusCode >= 400) {
        return rejectWithValue(
          fail(response.message || 'Failed to save port request'),
        )
      }

      const created = response.data?.twilio_port_request
      if (!created) {
        return rejectWithValue('Port request not found in the response')
      }

      return created
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to submit port request',
      )
    }
  },
)

export const refreshPortRequestStatusAction = createAsyncThunk<
  void,
  { portRequest: ITwilioPortRequest; clinicId: string },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/refreshPortRequestStatus',
  async ({ portRequest, clinicId }, { dispatch, rejectWithValue }) => {
    try {
      if (!portRequest.twilio_port_request_id) {
        return rejectWithValue('Missing port request id')
      }
      if (!portRequest.twilio_request_sid?.trim()) {
        await dispatch(fetchPhoneManagementData(clinicId)).unwrap()
        return
      }
      const twilioRes = await getPortRequestStatusFromTwilio(
        portRequest.twilio_request_sid,
      )
      if (
        twilioRes.statusCode !== 200 ||
        !twilioRes.data?.twilio_port_request
      ) {
        return rejectWithValue(
          fail(twilioRes.message || 'Failed to fetch status from Twilio'),
        )
      }
      const raw = twilioRes.data.twilio_port_request as unknown as Record<
        string,
        unknown
      >
      const twilioStatusStr = extractTwilioPortStatus(raw)
      const internalStatus = isTwilioPortCompleted(twilioStatusStr)
        ? 'Completed'
        : 'In Progress'
      const updateRes = await updatePortRequest(
        portRequest.twilio_port_request_id,
        {
          twilio_status: twilioStatusStr,
          status: internalStatus,
        },
      )
      if (updateRes.statusCode >= 400) {
        return rejectWithValue(
          fail(updateRes.message || 'Failed to update port request'),
        )
      }
      await dispatch(fetchPhoneManagementData(clinicId)).unwrap()
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to refresh port request status',
      )
    }
  },
)

export const cancelPortRequestAction = createAsyncThunk<
  void,
  { portRequest: ITwilioPortRequest; clinicId: string },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/cancelPortRequest',
  async ({ portRequest, clinicId }, { dispatch, rejectWithValue }) => {
    try {
      if (!portRequest.twilio_port_request_id) {
        return rejectWithValue('Missing port request id')
      }
      const ts = normalizeTwilioStatusKey(portRequest.twilio_status || '')
      const isPending = ts === 'pending'
      const updateRes = await updatePortRequest(
        portRequest.twilio_port_request_id,
        {
          status: 'Cancelled',
        },
      )
      if (updateRes.statusCode >= 400) {
        return rejectWithValue(
          fail(updateRes.message || 'Failed to cancel port request'),
        )
      }
      if (!isPending) {
        logger.info(
          'TODO in Portal Management: Admin will send an email to Twilio',
        )
      }
      await dispatch(fetchPhoneManagementData(clinicId)).unwrap()
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to cancel port request',
      )
    }
  },
)

export const submitFaxPortRequestDbAction = createAsyncThunk<
  ITwilioPortRequest,
  {
    clinicId: string
    clinicSnapshot?: ReturnType<typeof toPortRequestClinicSnapshot>
    phone_numbers: string[]
    phone_carrier: string
    representative_name: string
    representative_email: string
    wireless_account_number: string | null
    wireless_pin_or_ssn: string | null
  },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/submitFaxPortRequestDb',
  async (payload, { rejectWithValue }) => {
    try {
      const targetPortDate = new Date().toISOString()
      const clinicSnapshot = payload.clinicSnapshot
      if (!clinicSnapshot) {
        return rejectWithValue(
          'Clinic details are required to submit a fax port request.',
        )
      }

      const providerPayload = buildSinchPortProviderPayload({
        clinic: clinicSnapshot,
        phone_numbers: payload.phone_numbers,
        phone_carrier: payload.phone_carrier,
        representative_name: payload.representative_name,
        representative_email: payload.representative_email,
        wireless_account_number: payload.wireless_account_number,
        wireless_pin_or_ssn: payload.wireless_pin_or_ssn,
      })

      const response = await createPortRequest({
        clinic_id: payload.clinicId,
        phone_numbers: payload.phone_numbers,
        phone_carrier: payload.phone_carrier,
        twilio_port_document_id: null,
        twilio_request_sid: null,
        representative_name: payload.representative_name,
        representative_email: payload.representative_email,
        wireless_phone_number: null,
        wireless_account_number: payload.wireless_account_number,
        wireless_pin_or_ssn: payload.wireless_pin_or_ssn,
        status: PortRequestStatus['In Progress'],
        twilio_status: 'Pending',
        target_port_date: targetPortDate,
        channel: PortRequestChannel.fax,
        provider: PortRequestProvider.sinch,
        source_module: PortRequestSourceModule.fax_settings,
        review_status: PortRequestReviewStatus.requested,
        ...getLoggedInRequester(),
        provider_payload: providerPayload,
      })

      if (response.statusCode >= 400) {
        return rejectWithValue(
          fail(response.message || 'Failed to save fax port request'),
        )
      }

      const created = response.data?.twilio_port_request
      if (!created) {
        return rejectWithValue('Port request missing from response')
      }
      return created
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to submit fax port request',
      )
    }
  },
)

export const resubmitPortRequestAction = createAsyncThunk<
  ITwilioPortRequest,
  {
    portRequestId: string
    clinicId: string
    data: Partial<ITwilioPortRequest>
  },
  { rejectValue: RejectValue }
>(
  'api/settings/communication/twilio-port-request/resubmit',
  async ({ portRequestId, clinicId, data }, { dispatch, rejectWithValue }) => {
    try {
      const requester = getLoggedInRequester()
      const response = await resubmitPortRequest(portRequestId, {
        ...data,
        requested_by_user_id: requester.requested_by_user_id,
        requested_by_name: requester.requested_by_name,
      })
      if (response.statusCode >= 400) {
        return rejectWithValue(
          fail(response.message || 'Failed to resubmit port request'),
        )
      }
      const created = response.data?.twilio_port_request
      if (!created) {
        return rejectWithValue('Resubmitted port request missing from response')
      }
      await dispatch(fetchPhoneManagementData(clinicId)).unwrap()
      return created
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to resubmit port request',
      )
    }
  },
)

export const resubmitPortRequestDbAction = createAsyncThunk<
  ITwilioPortRequest,
  {
    portRequestId: string
    clinicId: string
    clinicName: string
    clinicSnapshot?: ReturnType<typeof toPortRequestClinicSnapshot>
    billingStatementFile?: File | null
    existingTwilioPortDocumentId?: string | null
    phone_numbers: string[]
    phone_carrier: string
    representative_name: string
    representative_email: string
    wireless_phone_number: string | null
    wireless_account_number: string | null
    wireless_pin_or_ssn: string | null
  },
  { rejectValue: RejectValue }
>(
  'api/settings/communication/twilio-port-request/resubmit-database',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      let twilioPortDocumentId =
        payload.existingTwilioPortDocumentId?.trim() || null

      if (payload.billingStatementFile) {
        const originalName = payload.billingStatementFile.name.replace(
          / /g,
          '_',
        )
        const filename =
          `billing-statement-${payload.clinicName}-${Date.now()}-${originalName}`
            .toLowerCase()
            .replace(/ /g, '_')

        const uploadUrlResponse = await generateUploadUrl(
          payload.billingStatementFile.type || 'application/pdf',
          filename,
          {
            folder: BLOB_FOLDERS.PORT_DOCUMENTS,
            clinic_id: payload.clinicId,
          },
        )
        if (uploadUrlResponse.statusCode !== 200) {
          return rejectWithValue(
            fail(uploadUrlResponse.message || 'Failed to get upload URL'),
          )
        }

        await uploadFile(
          uploadUrlResponse.data.upload_url,
          payload.billingStatementFile,
        )

        const readOnlyUrlResponse = await generateReadOnlyUrl(
          uploadUrlResponse.data.blob_name || filename,
        )
        if (readOnlyUrlResponse.statusCode !== 200) {
          return rejectWithValue(
            fail(
              readOnlyUrlResponse.message || 'Failed to generate read-only URL',
            ),
          )
        }

        const documentResponse = await createPortDocument(
          payload.clinicId,
          `PENDING_ADMIN_DOC_${Date.now()}`,
          'billing_statement',
          readOnlyUrlResponse.data.read_only_url,
        )
        if (documentResponse.statusCode !== 201) {
          return rejectWithValue(
            fail(documentResponse.message || 'Failed to save document'),
          )
        }

        twilioPortDocumentId =
          documentResponse.data.twilio_port_document?.twilio_port_document_id ||
          null
      }

      if (!twilioPortDocumentId) {
        return rejectWithValue('Billing statement is required to resubmit.')
      }

      const clinicSnapshot =
        payload.clinicSnapshot ||
        toPortRequestClinicSnapshot({
          clinic_name: payload.clinicName,
          address: '',
          address_line_2: null,
          city: '',
          state: '',
          zip: '',
          country_code: 'US',
        })
      if (!clinicSnapshot) {
        return rejectWithValue(
          'Clinic details are required to resubmit a port request.',
        )
      }

      return dispatch(
        resubmitPortRequestAction({
          portRequestId: payload.portRequestId,
          clinicId: payload.clinicId,
          data: {
            phone_numbers: payload.phone_numbers,
            phone_carrier: payload.phone_carrier,
            twilio_port_document_id: twilioPortDocumentId,
            representative_name: payload.representative_name,
            representative_email: payload.representative_email,
            wireless_phone_number: payload.wireless_phone_number,
            wireless_account_number: payload.wireless_account_number,
            wireless_pin_or_ssn: payload.wireless_pin_or_ssn,
            provider_payload: buildTwilioPortProviderPayload({
              clinic: clinicSnapshot,
              phone_numbers: payload.phone_numbers,
              phone_carrier: payload.phone_carrier,
              representative_name: payload.representative_name,
              representative_email: payload.representative_email,
              wireless_phone_number: payload.wireless_phone_number,
              wireless_account_number: payload.wireless_account_number,
              wireless_pin_or_ssn: payload.wireless_pin_or_ssn,
              twilio_port_document_id: twilioPortDocumentId,
              document_sids: [],
            }),
          },
        }),
      ).unwrap()
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to resubmit port request',
      )
    }
  },
)

export const resubmitFaxPortRequestDbAction = createAsyncThunk<
  ITwilioPortRequest,
  {
    portRequestId: string
    clinicId: string
    clinicSnapshot: ReturnType<typeof toPortRequestClinicSnapshot>
    phone_numbers: string[]
    phone_carrier: string
    representative_name: string
    representative_email: string
    wireless_account_number: string | null
    wireless_pin_or_ssn: string | null
  },
  { rejectValue: RejectValue }
>(
  'api/settings/communication/twilio-port-request/resubmit-fax-database',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      if (!payload.clinicSnapshot) {
        return rejectWithValue(
          'Clinic details are required to resubmit a fax port request.',
        )
      }

      return dispatch(
        resubmitPortRequestAction({
          portRequestId: payload.portRequestId,
          clinicId: payload.clinicId,
          data: {
            phone_numbers: payload.phone_numbers,
            phone_carrier: payload.phone_carrier,
            twilio_port_document_id: null,
            representative_name: payload.representative_name,
            representative_email: payload.representative_email,
            wireless_phone_number: null,
            wireless_account_number: payload.wireless_account_number,
            wireless_pin_or_ssn: payload.wireless_pin_or_ssn,
            provider_payload: buildSinchPortProviderPayload({
              clinic: payload.clinicSnapshot,
              phone_numbers: payload.phone_numbers,
              phone_carrier: payload.phone_carrier,
              representative_name: payload.representative_name,
              representative_email: payload.representative_email,
              wireless_account_number: payload.wireless_account_number,
              wireless_pin_or_ssn: payload.wireless_pin_or_ssn,
            }),
          },
        }),
      ).unwrap()
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to resubmit fax port request',
      )
    }
  },
)

export const releaseClinicNumberAction = createAsyncThunk<
  void,
  { clinicNumber: IClinicNumber; clinicId: string },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/releaseClinicNumber',
  async (
    { clinicNumber, clinicId },
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      if (!clinicNumber.clinic_number_id) {
        return rejectWithValue('Missing clinic number id')
      }
      const selectedClinic = (getState() as RootState).clinic.selectedClinic
      const sid = selectedClinic?.twilio_account_sid
      const token = selectedClinic?.twilio_auth_token
      const messageServiceSid = selectedClinic?.messaging_service_sid
      if (!sid || !token || !messageServiceSid) {
        return rejectWithValue(
          'Clinic Twilio credentials or messaging service are not configured',
        )
      }
      const phoneNumberForTwilio =
        clinicNumber.number_sid ||
        normalizePhoneNumber(clinicNumber.phone_number)
      const releaseRes = await releaseTwilioNumber({
        sid,
        token,
        phoneNumber: phoneNumberForTwilio,
        messageServiceSid,
      })
      if (releaseRes.statusCode >= 400) {
        return rejectWithValue(
          fail(releaseRes.message || 'Failed to release number in Twilio'),
        )
      }
      await dispatch(
        updateClinicNumberAction({
          id: clinicNumber.clinic_number_id,
          data: {
            released_date: new Date().toISOString(),
            status: 'Released',
            released_reason: 'Released by user',
          },
        }),
      ).unwrap()
      await dispatch(fetchPhoneManagementData(clinicId)).unwrap()
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to release clinic number',
      )
    }
  },
)

export const fetchCallFlowsAction = createAsyncThunk<
  ICallFlow[],
  string,
  { rejectValue: RejectValue }
>(
  'settingsCommunication/fetchCallFlows',
  async (clinicId, { rejectWithValue }) => {
    const res = await getCallFlows(clinicId)
    if (res.statusCode >= 400) {
      return rejectWithValue(fail(res.message))
    }
    return res.data.call_flows ?? []
  },
)

export const createCallFlowAction = createAsyncThunk<
  ICallFlow,
  {
    clinic_id: string
    name: string
    flow_type?: 'call' | 'sms'
    status?: string
    definition?: Record<string, unknown>
    clinic_number_ids?: string[]
  },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/createCallFlow',
  async (payload, { rejectWithValue }) => {
    const res = await createCallFlowRequest(payload)
    if (res.statusCode >= 400 || !res.data.call_flow) {
      return rejectWithValue(fail(res.message))
    }
    return res.data.call_flow as ICallFlow
  },
)

export const updateCallFlowAction = createAsyncThunk<
  ICallFlow,
  {
    id: string
    body: {
      name?: string
      status?: string
      definition?: Record<string, unknown>
      clinic_number_ids?: string[]
    }
  },
  { rejectValue: RejectValue }
>(
  'settingsCommunication/updateCallFlow',
  async ({ id, body }, { rejectWithValue }) => {
    const res = await updateCallFlowRequest(id, body)
    if (res.statusCode >= 400 || !res.data.call_flow) {
      return rejectWithValue(fail(res.message))
    }
    return res.data.call_flow as ICallFlow
  },
)

export const deleteCallFlowAction = createAsyncThunk<
  string,
  string,
  { rejectValue: RejectValue }
>('settingsCommunication/deleteCallFlow', async (id, { rejectWithValue }) => {
  const res = await deleteCallFlowRequest(id)
  if (res.statusCode >= 400) {
    return rejectWithValue(fail(res.message))
  }
  return id
})
